import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { getThinkingSteps, getThinkingTitle } from '../config/agentThinkingSteps';

export default function AIAgentThinkingSteps({
  isActive,
  agentMode = 'agent',
  isBuildingPlan = false,
  lightSurface = false,
  variant = 'compact',
}) {
  const steps = getThinkingSteps(agentMode, isBuildingPlan);
  const title = getThinkingTitle(agentMode, isBuildingPlan);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const isWorkspace = variant === 'workspace';

  React.useEffect(() => {
    if (!isActive) {
      setActiveIndex(0);
      return undefined;
    }

    setActiveIndex(0);
    const interval = setInterval(() => {
      setActiveIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, 1200);

    return () => clearInterval(interval);
  }, [isActive, steps.length, agentMode, isBuildingPlan]);

  if (!isActive) return null;

  const shellClass = lightSurface
    ? 'border-white/40 bg-white/70 backdrop-blur-md'
    : isWorkspace
      ? 'border-white/20 bg-white/10 backdrop-blur-lg'
      : 'border-white/15 bg-white/[0.06] backdrop-blur-md';

  const titleClass = lightSurface ? 'text-gray-600' : 'text-white/55';
  const doneClass = lightSurface ? 'text-emerald-700' : 'text-white/70';
  const currentClass = lightSurface ? 'text-blue-700' : 'text-white';
  const pendingClass = lightSurface ? 'text-gray-400' : 'text-white/40';

  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${
        isWorkspace ? 'max-w-[min(640px,88%)]' : ''
      } ${shellClass}`}
      role="status"
      aria-live="polite"
    >
      <p className={`m-0 mb-2 text-[0.62rem] font-semibold uppercase tracking-wider ${titleClass}`}>
        {title}
      </p>

      <ol className="m-0 p-0 list-none space-y-1">
        {steps.map((step, index) => {
          const isDone = index < activeIndex;
          const isCurrent = index === activeIndex;

          return (
            <li
              key={step.id}
              className={`flex items-center gap-2 text-[0.7rem] leading-snug transition-colors ${
                isDone ? doneClass : isCurrent ? currentClass : pendingClass
              } ${isCurrent ? 'font-medium' : ''}`}
            >
              <span className="w-3.5 h-3.5 shrink-0 grid place-items-center">
                {isDone ? (
                  <Check size={12} strokeWidth={2.5} className="opacity-80" />
                ) : isCurrent ? (
                  <Loader2 size={12} className="animate-spin opacity-90" />
                ) : (
                  <span className={`w-1 h-1 rounded-full ${lightSurface ? 'bg-gray-300' : 'bg-white/30'}`} />
                )}
              </span>
              <span>{step.label}{isCurrent ? '…' : ''}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
